import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateNumeric } from "@/lib/format";
import { ConfirmedBySession } from "@/components/ConfirmedBySession";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Users, Bookmark, Heart, MessageCircle, Mail, Loader2, CheckCircle2,
  Lightbulb, CalendarClock, Activity, Flag, UserCheck, CalendarDays,
} from "lucide-react";

const STATES = [
  { key: "proximo", label: "Próximos", icon: CalendarClock },
  { key: "en_curso", label: "En curso", icon: Activity },
  { key: "realizado", label: "Realizados", icon: Flag },
];

const STATE_BADGE = {
  proximo: { text: "Por iniciar", cls: "bg-[hsl(210,70%,45%)] text-white" },
  en_curso: { text: "En curso", cls: "bg-[hsl(145,42%,38%)] text-white" },
  realizado: { text: "Realizado", cls: "bg-muted text-muted-foreground" },
};

const waLink = (phone, text) => {
  const digits = String(phone || "").replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};

function Funnel({ paid, interested }) {
  return (
    <div className="grid grid-cols-2 gap-2 mt-3">
      <div className="rounded-lg bg-primary/5 border border-primary/10 p-2.5 text-center" data-testid="funnel-paid">
        <p className="font-display font-extrabold text-xl text-primary leading-none">{paid}</p>
        <p className="text-[11px] text-muted-foreground mt-1 flex items-center justify-center gap-1"><Users className="w-3 h-3" />Inscritos</p>
      </div>
      <div className="rounded-lg bg-muted/50 border border-border p-2.5 text-center" data-testid="funnel-interested">
        <p className="font-display font-extrabold text-xl leading-none">{interested}</p>
        <p className="text-[11px] text-muted-foreground mt-1 flex items-center justify-center gap-1"><Heart className="w-3 h-3" />En Lista de Deseos</p>
      </div>
    </div>
  );
}

function OccupancyBar({ booked, capacity }) {
  const pct = capacity > 0 ? Math.min(100, Math.round((booked / capacity) * 100)) : 0;
  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">Ocupación</span>
        <span className="font-semibold">{booked}/{capacity} ({pct}%)</span>
      </div>
      <div className="h-2.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function WorkshopCard({ w, onOpen }) {
  const badge = STATE_BADGE[w.estado] || STATE_BADGE.proximo;
  const showAlert = !w.sold_out && w.spots_left > 0 && w.interested.length > 0;
  return (
    <Card className="p-4 flex flex-col min-w-0 w-full" data-testid={`panel-card-${w.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-bold break-words" data-testid={`panel-name-${w.id}`}>{w.name}</h3>
          <p className="text-xs text-muted-foreground" data-testid={`panel-category-${w.id}`}>{w.category}</p>
        </div>
        <Badge className={`${badge.cls} shrink-0`}>{badge.text}</Badge>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid={`panel-dates-${w.id}`}>
        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-primary" />Inicio: <strong className="text-foreground">{w.first_start ? formatDateNumeric(w.first_start) : "—"}</strong></span>
        <span>Término: <strong className="text-foreground">{w.last_end ? formatDateNumeric(w.last_end) : "—"}</strong></span>
      </div>

      <OccupancyBar booked={w.booked} capacity={w.capacity} />

      {w.sold_out && (
        <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[hsl(145,42%,38%)]" data-testid={`sold-out-${w.id}`}>
          <CheckCircle2 className="w-4 h-4" />100% Vendido
        </div>
      )}

      <Funnel paid={w.booked} interested={w.interested.length} />

      {(w.estado === "en_curso" || w.estado === "realizado") && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`attendance-${w.id}`}>
          <UserCheck className="w-3.5 h-3.5 text-primary" />
          Asistencia: <span className="font-semibold text-foreground">{w.attended}/{w.attendance_total}</span>
        </div>
      )}

      {showAlert && (
        <div className="mt-3 rounded-lg bg-[hsl(38,92%,95%)] border border-[hsl(38,80%,70%)] text-[hsl(32,70%,32%)] p-2.5 text-xs flex gap-2"
          data-testid={`alert-${w.id}`}>
          <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Tienes <strong>{w.interested.length}</strong> alumno(s) interesado(s). ¡Contáctalos antes de que se agoten los cupos!</span>
        </div>
      )}

      <Button variant="outline" size="sm" className="mt-4 w-full" onClick={() => onOpen(w)} data-testid={`manage-students-${w.id}`}>
        <Users className="w-4 h-4 mr-1.5" />Gestión de alumnos
      </Button>
    </Card>
  );
}

function StudentsDialog({ w, onClose }) {
  const [reminding, setReminding] = useState(null);
  if (!w) return null;
  const remind = async (student) => {
    setReminding(student.user_id);
    try {
      const { data } = await api.post("/business/remind-interested", { workshop_id: w.id, user_id: student.user_id });
      toast.success(data.mocked
        ? "Recordatorio registrado (envío de correo en modo demo)"
        : `Recordatorio enviado a ${data.to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReminding(null); }
  };
  return (
    <Dialog open={!!w} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="students-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="pr-6">Gestión de alumnos</DialogTitle>
          <p className="text-sm font-semibold text-primary break-words" data-testid="students-dialog-workshop">{w.name}</p>
        </DialogHeader>
        <Tabs defaultValue="confirmados" className="min-w-0">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="confirmados" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="students-tab-confirmados">
              <Users className="w-4 h-4 mr-1.5" />Confirmados ({w.paid.length})
            </TabsTrigger>
            <TabsTrigger value="interesados" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="students-tab-interesados">
              <Heart className="w-4 h-4 mr-1.5" />Interesados ({w.interested.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="confirmados" className="pt-4">
            <ConfirmedBySession workshopId={w.id} />
          </TabsContent>

          <TabsContent value="interesados" className="pt-4">
            {w.sold_out && w.interested.length > 0 && (
              <div className="mb-3 rounded-lg bg-muted p-3 text-xs text-muted-foreground flex items-center gap-2" data-testid="sold-out-note">
                <CheckCircle2 className="w-4 h-4 text-[hsl(145,42%,38%)]" />Taller 100% vendido: las promociones de cupos están desactivadas.
              </div>
            )}
            <Table>
              <TableHeader><TableRow>
                <TableHead>Alumno</TableHead><TableHead>Email</TableHead>
                <TableHead>Teléfono</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {w.interested.map((s) => (
                  <TableRow key={s.user_id} data-testid={`interested-row-${s.user_id}`}>
                    <TableCell className="font-medium">{s.name || "—"}</TableCell>
                    <TableCell className="text-xs">{s.email}</TableCell>
                    <TableCell className="text-xs font-mono">{s.phone || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-2 justify-end">
                        {s.phone && (
                          <a href={waLink(s.phone, `¡Hola ${s.name || ""}! Vi que estás interesado en el taller "${w.name}". ¿Tienes alguna duda con los horarios o cupos?`)}
                            target="_blank" rel="noreferrer" data-testid={`wa-interested-${s.user_id}`}>
                            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-[hsl(145,42%,38%)]">
                              <MessageCircle className="w-3.5 h-3.5" />WhatsApp
                            </Button>
                          </a>
                        )}
                        <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={w.sold_out || reminding === s.user_id}
                          onClick={() => remind(s)} data-testid={`remind-interested-${s.user_id}`}>
                          {reminding === s.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                          Recordar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {w.interested.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Nadie tiene este taller en su lista de deseos.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export function WorkshopPanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("proximo");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/business/dashboard").then((r) => {
      setData(r.data);
      if (r.data.some((w) => w.estado === "en_curso")) setFilter("en_curso");
    });
  }, []);

  if (!data) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const counts = STATES.reduce((a, s) => ({ ...a, [s.key]: data.filter((w) => w.estado === s.key).length }), {});
  const filtered = data.filter((w) => w.estado === filter);

  return (
    <div className="pt-6">
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex-wrap h-auto">
          {STATES.map((s) => (
            <TabsTrigger key={s.key} value={s.key} data-testid={`panel-filter-${s.key}`}>
              <s.icon className="w-4 h-4 mr-1.5" />{s.label} ({counts[s.key] || 0})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        {filtered.map((w) => <WorkshopCard key={w.id} w={w} onOpen={setDetail} />)}
        {filtered.length === 0 && (
          <p className="text-muted-foreground col-span-full py-12 text-center" data-testid="panel-empty">
            No tienes talleres en este estado.
          </p>
        )}
      </div>

      <StudentsDialog w={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
